import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import { Button } from 'primereact/button';
import { Link } from 'react-router-dom';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { SplitButton } from 'primereact/splitbutton';
import { resetSurveyRespondentStatus } from '../../../graphql/survey/Mutation';
import { growlErrors } from '../../../utils/NotificationUtil';
import { intlShape, growlShape } from '../../../components/prop-types';
import { withGrowl } from '../../../components/GrowlNotifications';
import { withAccount } from '../../../components/AccountContext';


const getItemStatus = {
  NEW: (
    <FormattedMessage id="candidate.status.NEW" defaultMessage="NEW">
      {text => <span className="filter is-active">{text}</span>}
    </FormattedMessage>
  ),
  INVITED: (
    <FormattedMessage id="candidate.status.INVITED" defaultMessage="INVITED">
      {text => <span className="filter filter--info is-active">{text}</span>}
    </FormattedMessage>
  ),
  STARTED: (
    <FormattedMessage id="candidate.status.STARTED" defaultMessage="IN PROGRESS">
      {text => <span className="filter filter--warning is-active">{text}</span>}
    </FormattedMessage>
  ),
  DECLINED: (
    <FormattedMessage id="candidate.status.DECLINED" defaultMessage="DECLINED">
      {text => <span className="filter filter--error is-active">{text}</span>}
    </FormattedMessage>
  ),
  EXPIRED: (
    <FormattedMessage id="candidate.status.EXPIRED" defaultMessage="EXPIRED">
      {text => <span className="filter filter--error is-active">{text}</span>}
    </FormattedMessage>
  ),
  SUBMITTED: (
    <FormattedMessage id="candidate.status.SUBMITTED" defaultMessage="ASSESSED">
      {text => <span className="filter filter--success is-active">{text}</span>}
    </FormattedMessage>
  ),
};

const messages = defineMessages({
  managers: {
    id: '360.managers',
    defaultMessage: 'Bosses',
  },
  peers: {
    id: '360.peers',
    defaultMessage: 'Peers',
  },
  directReports: {
    id: '360.directReports',
    defaultMessage: 'Direct Reports',
  },
  others: {
    id: '360.others',
    defaultMessage: 'Others',
  },
  resetStatus: {
    id: 'assessment.resetStatus',
    defaultMessage: 'Reset status',
  },
  previewInvite: {
    id: 'assessment.invite.preview',
    defaultMessage: 'Preview Invite',
  },
  previewReminder: {
    id: 'assessment.remind.preview',
    defaultMessage: 'Preview Reminder',
  },
});

class SurveyRespondentCard extends Component {
  constructor() {
    super();
    this.acceptResetStatus = this.acceptResetStatus.bind(this);
    this.changeRespondentCategory = this.changeRespondentCategory.bind(this);
    this.isSelected = this.isSelected.bind(this);
    this.previewSurvey = this.previewSurvey.bind(this);
    this.handlePreviewInviteEmail = this.handlePreviewInviteEmail.bind(this);
    this.handlePreviewRemindEmail = this.handlePreviewRemindEmail.bind(this);
    this.state = {
      isLoading: false,
    };
  }

  async acceptResetStatus() {
    const { assessment, currentAccount, growl } = this.props;
    const { id } = assessment;
    try {
      await resetSurveyRespondentStatus({
        surveyRespondentId: id,
        accountId: currentAccount,
      });
      this.setState({ isLoading: true });
    } catch (errors) {
      growlErrors(growl, errors);
    }
  }

  changeRespondentCategory(e) {
    const {
      assessment, type, onChangeRespondentCategory,
    } = this.props;
    const { status: asmtStatus, respondent } = assessment;
    const newType = e.target.value;

    onChangeRespondentCategory(respondent, asmtStatus, type, newType);
  }

  isSelected(category) {
    const { type } = this.props;
    return type === category;
  }

  async previewSurvey() {
    const { assessment } = this.props;
    window.open(assessment.startUrl, '_blank');
  }

  handlePreviewInviteEmail() {
    const { assessment } = this.props;
    window.open(assessment.previewInviteUrl, '_blank');
  }

  handlePreviewRemindEmail() {
    const { assessment } = this.props;
    window.open(assessment.previewReminderUrl, '_blank');
  }

  render() {
    const {
      isEdit,
      type,
      onRemove,
      onChangeCheckedRespondent,
      currentAccount,
      assessment,
      intl: { formatMessage },
      reportObservers,
    } = this.props;
    const { status: asmtStatus, respondent } = assessment;
    const detailURL = `/accounts/${currentAccount}/respondents/${respondent.id}/`;
    if (!respondent) return null;
    const assessmentStatus = asmtStatus || 'NEW';

    const { isLoading } = this.state;
    const safeStatus = `${assessmentStatus}` === '' ? 'unknown' : assessmentStatus.toLowerCase();

    const categories = [
      {
        value: 'managers',
        label: formatMessage(messages.managers),
      },
      {
        value: 'peers',
        label: formatMessage(messages.peers),
      },
      {
        value: 'directReports',
        label: formatMessage(messages.directReports),
      },
      {
        value: 'others',
        label: formatMessage(messages.others),
      },
    ];

    const items = [
      {
        label: formatMessage(messages.previewInvite),
        icon: '',
        command: this.handlePreviewInviteEmail,
      },
      {
        label: formatMessage(messages.previewReminder),
        icon: '',
        command: this.handlePreviewRemindEmail,
      },
    ];
    return (
      <div className="p-grid p-nogutter vertical-padding border-bottom">
        <div className="p-col-8">
          {assessmentStatus === 'SUBMITTED' && !isEdit && type !== 'self' && (
            <Checkbox
              onChange={onChangeCheckedRespondent}
              value={respondent.id}
              checked={reportObservers.includes(respondent.id)}
            />
          )}

          <div className="survey-respondent">
            <Link to={detailURL} className="survey-respondent-name">
              <div data-t="cs-item-name" className="">
                {`${respondent.firstName} ${respondent.lastName}`}
                {' '}
              </div>
            </Link>
            <div className="survey-respondent-email">{respondent.email}</div>
          </div>

          {(assessmentStatus === 'DECLINED' && !isEdit) && (
            <div className="p-col">
              <SplitButton
                label={formatMessage(messages.resetStatus)}
                onClick={this.acceptResetStatus}
                model={items}
                disabled={isLoading}
              />
            </div>
          )}

          {isEdit && (
            <Dropdown
              options={categories}
              onChange={this.changeRespondentCategory}
              value={type}
            />
          )}
        </div>
        <div className="p-col-4 align-right">
          <div className={`assessment assessment-${safeStatus}`}>{getItemStatus[assessmentStatus]}</div>

          {isEdit && (
            <div className="">
              <Button
                type="button"
                className="p-button-secondary"
                icon="fa fa-trash"
                onClick={() => onRemove(respondent.id)}
              />
            </div>
          )}
        </div>
      </div>
    );
  }
}

SurveyRespondentCard.propTypes = {
  assessment: PropTypes.objectOf(PropTypes.any).isRequired,
  isEdit: PropTypes.bool,
  onRemove: PropTypes.func,
  onChangeRespondentCategory: PropTypes.func,
  onChangeCheckedRespondent: PropTypes.func,
  type: PropTypes.string.isRequired,
  currentAccount: PropTypes.string.isRequired,
  intl: intlShape.isRequired,
  growl: growlShape.isRequired,
  reportObservers: PropTypes.arrayOf(PropTypes.string),
};

SurveyRespondentCard.defaultProps = {
  onRemove: null,
  onChangeRespondentCategory: null,
  onChangeCheckedRespondent: null,
  isEdit: false,
  reportObservers: [],
};

export default withAccount(
  withGrowl(
    injectIntl(SurveyRespondentCard),
  ),
);
